import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { WorkspaceOAuthCallback } from "./pages/WorkspaceOAuthCallback";
import { installHostModules } from "./runtime/plugins";
import { loadAppModules } from "./modules";
import "./app.css";

// Expose curated host modules (UI / runtime / hooks / utils) on
// `window.__magnis_host`. Plugins loaded later import bare specifiers
// like `@magnis/host/ui`; the backend transpile endpoint rewrites
// those to tiny shim modules that read from this global, so every
// plugin shares the SAME React + react-query + UI instances.
installHostModules();

// crypto.randomUUID() is only exposed on secure contexts (HTTPS or
// localhost). Devs hitting the Vite dev server over Tailscale (e.g.
// http://u3775:5183) get a plain HTTP page where `randomUUID` is
// undefined, and any code that calls it (episode creation, etc.)
// crashes with "is not a function". `crypto.getRandomValues` is
// always available, so we polyfill randomUUID on top of it.
if (typeof crypto !== "undefined" && typeof crypto.randomUUID !== "function") {
  Object.defineProperty(crypto, "randomUUID", {
    value: function polyfilledRandomUUID(): `${string}-${string}-${string}-${string}-${string}` {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      // RFC 4122 v4: set version + variant bits.
      bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
      bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
      const hex: string[] = [];
      for (const b of bytes) {
        hex.push(b.toString(16).padStart(2, "0"));
      }
      return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
    },
  });
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}
const root: HTMLElement = rootElement;
const isWorkspaceOAuthCallback =
  window.location.pathname === "/oauth/callback";

// Source authorization (Stage 6): the host runs the whole ceremony at
// `/auth/sources/<id>/{start,callback}` and redirects back with
// `?source_connected=<id>` (or `?source_auth_error=`). The connect flow opens
// this in a POPUP (browser window / Tauri webview), so when this load IS that
// post-OAuth redirect inside a popup, close it — the opener already learned of the
// connection via the `source.account.connected` WS event. If it landed in the
// MAIN window instead (popup blocked / direct nav), just clean the URL + render.
function isOAuthRedirect(): boolean {
  const p = new URLSearchParams(window.location.search);
  return p.has("source_connected") || p.has("source_auth_error");
}

async function closeOAuthPopupIfAny(): Promise<boolean> {
  if (!isOAuthRedirect()) return false;
  // Browser popup → it has an opener.
  if (window.opener && window.opener !== window) {
    window.close();
    return true;
  }
  // Tauri → a dedicated oauth webview (not the "main" window); close it.
  if ("__TAURI_INTERNALS__" in window || "__TAURI__" in window) {
    try {
      const mod = (await import("@tauri-apps/api/window")) as {
        getCurrentWindow: () => { label: string; close: () => Promise<void> };
      };
      const w = mod.getCurrentWindow();
      if (w.label !== "main") {
        await w.close();
        return true;
      }
    } catch {
      /* not closable here — fall through to render */
    }
  }
  return false;
}

async function bootRoot(): Promise<void> {
  if (isWorkspaceOAuthCallback) {
    ReactDOM.createRoot(root).render(
      <React.StrictMode>
        <WorkspaceOAuthCallback />
      </React.StrictMode>,
    );
    return;
  }
  if (await closeOAuthPopupIfAny()) return; // popup closed itself; nothing to render
  if (isOAuthRedirect()) {
    // Landed in the main window — drop the one-shot query param.
    window.history.replaceState({}, "", window.location.pathname);
  }
  await loadAppModules();
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

void bootRoot();
